import bidiFactory, { type BidiInstance } from 'bidi-js';
import { flow } from 'lodash';
import type {
  ParsedSegment,
  StringParser,
  StringParserParseOptions,
} from './types';
import {
  isLineBreak,
  normalizeSegmentsToLines,
} from './normalizeSegmentsToLines';

type Params = {
  locale: string;
  granularity: 'character' | 'word';
  normalizers?: Array<(segments: Array<ParsedSegment>) => Array<ParsedSegment>>;
};

const granularityMap: Map<
  Params['granularity'],
  Intl.SegmenterOptions['granularity']
> = new Map([
  ['character', 'grapheme'],
  ['word', 'word'],
]);

let bidi: BidiInstance | undefined;

export const createStringParser = (params: Params): StringParser => {
  const { locale, granularity: _granularity, normalizers = [] } = params;

  const granularity = granularityMap.get(_granularity);

  if (!granularity) {
    throw new Error('StringParser: granularity is undefined!');
  }

  if (!bidi) {
    bidi = bidiFactory();
  }

  const segmenter = new Intl.Segmenter(locale, {
    granularity,
  });

  const normalize = flow(...normalizers, normalizeSegmentsToLines);

  const defaultParseOptions: StringParserParseOptions = {
    textDirection: 'ltr',
  };

  return {
    parse: (str, options) => {
      const { textDirection } = options ?? defaultParseOptions;

      let preparedString = str;

      if (granularity === 'grapheme') {
        if (!bidi) {
          throw new Error('StringParser: bidi is undefined!');
        }

        try {
          /**
            Reorder algorithm explanation:

            Let's have the user input string "Hello שלום" (original string).

            "bidi-js" - is responsible for handling bidirectional text in LTR and RTL contexts.
            Operation level: character-level (position in the stored source string).

            "bidi-js" helps us to find the flip ranges in the ORIGINAL string.
            We need to flip, for example, the range between "ש" and "ם".
            In this case, the flip range indexes are [6, 9] (6 refers to "ש" and 9 refers to "ם").

            "Intl.Segmenter" - is responsible for CORRECT splitting the string into segments.
            Operation level: visual graphemes (what user sees in the browser).

            "Intl.Segmenter" takes into consideration (aka why not `str.split()`):
              - CJK (Chinese, Japanese, Korean) characters
              - Emojis (including complex ones like 👨‍👩‍👧‍👦)
              - Ligatures (e.g., "æ", "œ")
              - Accented characters and diacritics (e.g., "café", "naïve")
              - Letters from any language
              - Correct split by words where `.split()` fails (sentenses without spaces between words)
              - Mixed langs in 1 sentence (also with mixed directions)
          */

          const embeddingLevels = bidi.getEmbeddingLevels(str, textDirection);

          preparedString = bidi.getReorderedString(str, embeddingLevels);
        } catch (error) {}
      }

      let segments = Array.from(segmenter.segment(preparedString), segment => ({
        data: segment.segment,
        isVisible: isVisibleSegment(segment.segment),
      }));

      if (granularity === 'grapheme' && textDirection === 'rtl') {
        /**
         * We need to flip, because the browser does the flip as well when the text direction is RTL.
         */
        segments = reverseBetweenLineBreaks(segments);
      }

      return {
        segmentsBrokenByLines: normalize(segments),
      };
    },
  };
};

const reverseBetweenLineBreaks = (segments: Array<ParsedSegment>) => {
  const reversedSegments = [];
  const n = segments.length;
  let start = 0;

  for (let i = 0; i < n; i++) {
    if (isLineBreak(segments[i].data)) {
      if (start < i) {
        reversedSegments.push(
          ...segments.slice(start, i).reverse(),
          segments[i],
        );
      } else {
        reversedSegments.push(segments[i]);
      }
      start = i + 1;
    }
  }

  if (start < n) {
    reversedSegments.push(...segments.slice(start).reverse());
  }

  return reversedSegments;
};

export const isVisibleSegment = (segment: string) => {
  if (segment === '') {
    return false;
  }
  const invisiblePattern = /^(\s|\u200B|\u200C|\u200D|\uFEFF)+$/;
  return !invisiblePattern.test(segment);
};
